import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL?.endsWith("/api") ? BACKEND_URL : `${BACKEND_URL}/api`;

/**
 * Match-day roster management.
 *
 * Layering:
 * - Shooter = global person (never deleted from here)
 * - League  = evolving club/series roster (optional link)
 * - Match   = day-of snapshot; can have guests not on the league
 *
 * Sync from league is additive only. Promote grows the league over time.
 */
const MatchRoster = ({ matchId, onRosterChange }) => {
  const { isAdmin } = useAuth();
  const [match, setMatch] = useState(null);
  const [league, setLeague] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [roster, setRoster] = useState(null);
  const [allShooters, setAllShooters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [linkLeagueId, setLinkLeagueId] = useState("");
  const [syncing, setSyncing] = useState(false);

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 3500);
  };

  const load = useCallback(async () => {
    try {
      const [matchRes, rosterRes, shootersRes, leaguesRes] = await Promise.all([
        axios.get(`${API}/matches/${matchId}`),
        axios.get(`${API}/matches/${matchId}/roster`),
        axios.get(`${API}/shooters`),
        axios.get(`${API}/leagues`).catch(() => ({ data: [] })),
      ]);

      setMatch(matchRes.data);
      setRoster(rosterRes.data);
      setAllShooters(shootersRes.data || []);
      setLeagues(leaguesRes.data || []);
      setLinkLeagueId(matchRes.data.league_id || "");

      if (matchRes.data.league_id) {
        try {
          const lr = await axios.get(
            `${API}/leagues/${matchRes.data.league_id}`
          );
          setLeague(lr.data);
        } catch {
          setLeague(null);
        }
      } else {
        setLeague(null);
      }

      setError(null);
      if (onRosterChange) onRosterChange(rosterRes.data);
    } catch (err) {
      console.error("Failed to load match roster:", err);
      setError(err.response?.data?.detail || "Failed to load match roster.");
    } finally {
      setLoading(false);
    }
  }, [matchId, onRosterChange]);

  useEffect(() => {
    load();
  }, [load]);

  const rosterIdSet = new Set(
    (roster?.members || []).map((m) => m.shooter.id)
  );

  const availableToAdd = allShooters.filter((s) => !rosterIdSet.has(s.id));

  const leagueIdSet = new Set(league?.roster_shooter_ids || []);

  const toggleSelected = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAddSelected = async () => {
    if (selectedIds.length === 0 && !newName.trim()) {
      setError("Select at least one existing shooter or enter a new name.");
      return;
    }
    setAdding(true);
    setError(null);
    try {
      const body = {
        shooter_ids: selectedIds,
        new_shooters: newName.trim() ? [{ name: newName.trim() }] : [],
      };
      const res = await axios.post(`${API}/matches/${matchId}/roster`, body);
      setRoster(res.data);
      setSelectedIds([]);
      setNewName("");
      const shootersRes = await axios.get(`${API}/shooters`);
      setAllShooters(shootersRes.data || []);
      showSuccess("Roster updated");
      if (onRosterChange) onRosterChange(res.data);
    } catch (err) {
      console.error("Add to roster failed:", err);
      setError(err.response?.data?.detail || "Failed to update roster.");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (member) => {
    const sid = member.shooter.id;
    const name = member.shooter.name;
    let deleteScores = false;

    if (member.has_scores) {
      const ok = window.confirm(
        `"${name}" has ${member.score_count} score(s) in this match.\n\n` +
          "OK = remove from roster AND delete their scores for THIS match only.\n" +
          "Cancel = leave them on the roster.\n\n" +
          "Global profile and league roster are never deleted from here."
      );
      if (!ok) return;
      deleteScores = true;
    } else {
      const ok = window.confirm(
        `Remove "${name}" from this match roster?\n\nProfile and league stay unchanged.`
      );
      if (!ok) return;
    }

    setBusyId(sid);
    setError(null);
    try {
      await axios.delete(
        `${API}/matches/${matchId}/roster/${sid}?delete_scores=${deleteScores}`
      );
      await load();
      showSuccess(
        deleteScores
          ? `Removed ${name} and their scores from this match`
          : `Removed ${name} from match roster`
      );
    } catch (err) {
      console.error("Remove from roster failed:", err);
      setError(err.response?.data?.detail || "Failed to remove from roster.");
    } finally {
      setBusyId(null);
    }
  };

  const handleAddScoredToRoster = async (member) => {
    setBusyId(member.shooter.id);
    setError(null);
    try {
      const res = await axios.post(`${API}/matches/${matchId}/roster`, {
        shooter_ids: [member.shooter.id],
        new_shooters: [],
      });
      setRoster(res.data);
      showSuccess(`Added ${member.shooter.name} to formal roster`);
      if (onRosterChange) onRosterChange(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add to roster.");
    } finally {
      setBusyId(null);
    }
  };

  const handleSyncFromLeague = async () => {
    setSyncing(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/matches/${matchId}/roster/sync-from-league`
      );
      setRoster(res.data);
      showSuccess("Pulled new league members onto this match (additive)");
      if (onRosterChange) onRosterChange(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Sync from league failed.");
    } finally {
      setSyncing(false);
    }
  };

  const handlePromote = async (member) => {
    setBusyId(member.shooter.id);
    setError(null);
    try {
      await axios.post(
        `${API}/matches/${matchId}/roster/${member.shooter.id}/promote-to-league`
      );
      // Refresh league membership for badge display
      if (match?.league_id) {
        const lr = await axios.get(`${API}/leagues/${match.league_id}`);
        setLeague(lr.data);
      }
      showSuccess(
        `Added ${member.shooter.name} to the league for future matches`
      );
    } catch (err) {
      setError(err.response?.data?.detail || "Promote to league failed.");
    } finally {
      setBusyId(null);
    }
  };

  const handleLinkLeague = async () => {
    setSyncing(true);
    setError(null);
    try {
      const res = await axios.put(`${API}/matches/${matchId}/league`, {
        league_id: linkLeagueId || null,
        pull_roster: Boolean(linkLeagueId),
      });
      setMatch(res.data);
      if (res.data.league_id) {
        const lr = await axios.get(`${API}/leagues/${res.data.league_id}`);
        setLeague(lr.data);
      } else {
        setLeague(null);
      }
      await load();
      showSuccess(
        linkLeagueId
          ? "Linked league and pulled members onto match roster"
          : "Unlinked league (match roster kept)"
      );
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update league link.");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <p className="text-ink-500 p-4">Loading roster...</p>;
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="alert-error" role="alert">
          {typeof error === "string" ? error : JSON.stringify(error)}
        </div>
      )}
      {success && (
        <div className="alert-success" role="status">
          {success}
        </div>
      )}

      {/* League link panel */}
      <div className="card card-pad">
        <h2 className="section-title mb-1">League link</h2>
        <p className="text-sm text-ink-500 mb-4">
          Optional. Link this match to a league so you can seed and grow a shared
          season roster. The match roster stays a day-of snapshot.
        </p>
        {league ? (
          <p className="text-sm mb-3">
            Linked to{" "}
            <Link
              to="/leagues"
              className="font-medium text-brand-600 hover:underline"
            >
              {league.name}
            </Link>
            {league.season ? ` (${league.season})` : ""} ·{" "}
            {(league.roster_shooter_ids || []).length} on league roster
          </p>
        ) : (
          <p className="text-sm text-ink-500 mb-3">Not linked to a league.</p>
        )}

        {isAdmin() && (
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="field-label">League</label>
              <select
                value={linkLeagueId}
                onChange={(e) => setLinkLeagueId(e.target.value)}
                className="select min-w-[14rem]"
              >
                <option value="">— None —</option>
                {leagues.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                    {l.season ? ` (${l.season})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              disabled={syncing}
              onClick={handleLinkLeague}
              className="btn-secondary btn-sm"
            >
              {syncing ? "Saving..." : "Save link"}
            </button>
            {match?.league_id && (
              <button
                type="button"
                disabled={syncing}
                onClick={handleSyncFromLeague}
                className="btn-primary btn-sm"
                title="Add league members who are not yet on this match (never removes anyone)"
              >
                {syncing ? "Syncing..." : "Pull new from league"}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="card card-pad">
        <h2 className="section-title mb-1">Match Roster</h2>
        <p className="text-sm text-ink-500 mb-4">
          Who is competing in <strong>this match</strong>. Score entry prefers
          this list when it is non-empty. Removing someone here never deletes
          their global profile or league membership.
        </p>

        {(roster?.members || []).length === 0 ? (
          <p className="text-ink-500 text-sm mb-4">
            No formal roster yet. Link a league and pull members, add shooters
            below, or enter scores for anyone from the full directory.
          </p>
        ) : (
          <div className="table-wrap mb-4">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>NRA</th>
                  <th>Rating</th>
                  <th>Scores</th>
                  <th>League</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {roster.members.map((m) => {
                  const onLeague = leagueIdSet.has(m.shooter.id);
                  return (
                    <tr key={m.shooter.id}>
                      <td className="font-medium">
                        <Link
                          to={`/shooters/${m.shooter.id}`}
                          className="text-brand-600 hover:underline"
                        >
                          {m.shooter.name}
                        </Link>
                      </td>
                      <td className="text-ink-500">
                        {m.shooter.nra_number || "—"}
                      </td>
                      <td className="text-ink-500">
                        {m.shooter.rating || "—"}
                      </td>
                      <td>
                        {m.has_scores ? (
                          <span className="text-emerald-700">{m.score_count}</span>
                        ) : (
                          <span className="text-ink-400">0</span>
                        )}
                      </td>
                      <td>
                        {!match?.league_id ? (
                          <span className="text-ink-400">—</span>
                        ) : onLeague ? (
                          <span className="badge-brand">
                            League
                          </span>
                        ) : (
                          <span className="badge-amber">
                            Match guest
                          </span>
                        )}
                      </td>
                      <td className="text-right space-x-3">
                        {isAdmin() && (
                          <>
                            <Link
                              to={`/scores/add/${matchId}?shooter=${m.shooter.id}`}
                              className="text-emerald-600 hover:text-emerald-800"
                            >
                              Scores
                            </Link>
                            {match?.league_id && !onLeague && (
                              <button
                                type="button"
                                disabled={busyId === m.shooter.id}
                                onClick={() => handlePromote(m)}
                                className="text-brand-600 hover:text-brand-800 disabled:opacity-50"
                                title="Add this person to the league for future matches"
                              >
                                Promote to league
                              </button>
                            )}
                            <button
                              type="button"
                              disabled={busyId === m.shooter.id}
                              onClick={() => handleRemove(m)}
                              className="text-rose-600 hover:text-rose-800 disabled:opacity-50"
                            >
                              {busyId === m.shooter.id ? "..." : "Remove"}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {(roster?.scored_but_not_on_roster || []).length > 0 && (
          <div className="mb-6 border border-amber-200 bg-amber-50 rounded-xl p-4">
            <h3 className="font-medium text-amber-900 mb-2">
              Scored but not on formal roster
            </h3>
            <ul className="space-y-2">
              {roster.scored_but_not_on_roster.map((m) => (
                <li
                  key={m.shooter.id}
                  className="flex flex-wrap items-center justify-between gap-2 text-sm"
                >
                  <span>
                    {m.shooter.name}{" "}
                    <span className="text-ink-500">
                      ({m.score_count} score
                      {m.score_count === 1 ? "" : "s"})
                    </span>
                  </span>
                  {isAdmin() && (
                    <span className="space-x-3">
                      <button
                        type="button"
                        disabled={busyId === m.shooter.id}
                        onClick={() => handleAddScoredToRoster(m)}
                        className="text-brand-700 hover:underline disabled:opacity-50"
                      >
                        Add to roster
                      </button>
                      {match?.league_id && (
                        <button
                          type="button"
                          disabled={busyId === m.shooter.id}
                          onClick={() => handlePromote(m)}
                          className="text-brand-700 hover:underline disabled:opacity-50"
                        >
                          Promote to league
                        </button>
                      )}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {isAdmin() && (
          <div className="border-t border-ink-100 pt-4 space-y-4">
            <h3 className="section-title">Add to this match</h3>

            <div>
              <label className="field-label">
                Create new shooter on this roster
              </label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Full name"
                className="input w-full md:w-1/2"
              />
            </div>

            {availableToAdd.length > 0 && (
              <div>
                <label className="field-label">
                  Or pick from directory ({availableToAdd.length} available)
                </label>
                <div className="max-h-48 overflow-y-auto rounded-lg border border-ink-200 p-2 space-y-1">
                  {availableToAdd.map((s) => (
                    <label
                      key={s.id}
                      className="flex items-center gap-2 text-sm px-2 py-1 hover:bg-ink-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(s.id)}
                        onChange={() => toggleSelected(s.id)}
                      />
                      <span>{s.name}</span>
                      {s.nra_number && (
                        <span className="text-ink-400">NRA {s.nra_number}</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={handleAddSelected}
              disabled={adding}
              className="btn-primary"
            >
              {adding ? "Adding..." : "Add to Roster"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchRoster;
